import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Card, Form } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { FaPlus } from "react-icons/fa";
import { useNavigate } from "react-router";
import { z } from "zod";

import {
    AppCollection,
    AppDocument,
    useAppDocument,
} from "../../api/firestoreWrappers.generated.jsx";
import { Breadcrumbs, breadcrumbPaths } from "../../components/Breadcrumbs";
import CancelButton from "../../components/CancelButton";
import TextFormGroup from "../../components/FormGroups/TextFormGroup.jsx";
import Layout, { Title } from "../../components/Layout/Layout";
import urls from "../../urls.jsx";

function pickDefined(obj) {
    if (!obj || typeof obj !== "object") return obj;

    const result = {};

    for (const [key, value] of Object.entries(obj)) {
        if (value === undefined) continue;

        if (typeof value === "object" && !Array.isArray(value)) {
            const nested = pickDefined(value);
            if (Object.keys(nested).length > 0) {
                result[key] = nested;
            }
        } else {
            result[key] = value;
        }
    }

    return result;
}

export default function AppCreatePage() {
    const navigate = useNavigate();
    const [demoApp] = useAppDocument("8asQEUzLViOECksM22Oh");
    const createAppConfigSchema = () =>
        z.object({
            name: z.string().min(1, { message: "Please enter a name" }),
        });

    const {
        control,
        handleSubmit,
        formState: { errors, isDirty, isSubmitting },
    } = useForm({
        resolver: zodResolver(createAppConfigSchema()),
    });

    const onSubmit = async (formData) => {
        const name = formData.name;
        try {
            // 1) create the new location
            const newAppId = await AppCollection().add({
                name,
                description: "",
                ...pickDefined({
                    name,
                    description: "",
                    ...pickDefined({
                        provider: demoApp?.provider && {
                            name: demoApp.provider.name,
                        },

                        horizontal_overlap: demoApp?.horizontal_overlap,
                        vertical_overlap: demoApp?.vertical_overlap,
                        working_distance: demoApp?.working_distance,
                        dependencies: demoApp?.dependencies,

                        appStarter: demoApp?.appStarter && {
                            bootDiskMib: demoApp.appStarter.bootDiskMib,
                            cpu: demoApp.appStarter.cpu,
                            docker_image: demoApp.appStarter.docker_image,
                            memory: demoApp.appStarter.memory,
                            prefix: demoApp.appStarter.prefix,
                        },

                        settings: demoApp?.settings && {
                            routeplanner: demoApp.settings.routeplanner && {
                                working_distance:
                                    demoApp.settings.routeplanner
                                        .working_distance,
                                vertical_overlap:
                                    demoApp.settings.routeplanner
                                        .vertical_overlap,
                                horizontal_overlap:
                                    demoApp.settings.routeplanner
                                        .horizontal_overlap,
                            },
                        },
                    }),
                }),
            });

            // 2) write back the generated id into the document
            await AppDocument(newAppId).update({ id: newAppId });

            // 3) navigate & mark saved
            navigate(urls.appSettings(newAppId));
        } catch (err) {
            console.error("Failed to create & save new location:", err);
        }
    };

    return (
        <Layout>
            <title>New app | MyCorvus</title>
            <Breadcrumbs path={breadcrumbPaths.globalAppCreate} />
            <Card className="mb-3">
                <Title title={`Create App`} />
                <Card.Body className="p-4 py-0">
                    <Form onSubmit={handleSubmit(onSubmit)}>
                        <TextFormGroup
                            label="App Name"
                            name="name"
                            control={control}
                            errors={errors}
                        />
                        <Button
                            variant="success"
                            disabled={isSubmitting || !isDirty}
                            type="submit"
                            className="mb-3 mt-1"
                        >
                            <FaPlus className="mb-1 me-1" />
                            {isSubmitting ? "Creating..." : "Create"}
                        </Button>
                        <CancelButton
                            className="ms-2 mb-3 mt-1"
                            to={urls.allApps()}
                        />
                    </Form>
                </Card.Body>
            </Card>
        </Layout>
    );
}
